import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth, formatApiErrorDetail } from '../../contexts/AuthContext';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Checkbox } from '../../components/ui/checkbox';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { toast } from 'sonner';
import { Shield, User, Briefcase } from 'lucide-react';

export const LoginPage = () => {
  const [portalType, setPortalType] = useState('user');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [rememberMe, setRememberMe] = useState(false);
  const [loading, setLoading] = useState(false);
  const { login, logout } = useAuth();
  const navigate = useNavigate();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const user = await login(email, password, rememberMe);
      
      // Check if admin portal is used by non-admin/super_admin
      if (portalType === 'admin' && user.role !== 'admin' && user.role !== 'super_admin') {
        toast.error('This account does not have admin privileges. Please use the User Portal tab.');
        await logout();
        setLoading(false);
        return;
      }
      
      // Check if agent portal is used by non-agent
      if (portalType === 'agent' && user.role !== 'agent') {
        toast.error('This account is not an agent. Please use the User Portal tab.');
        await logout();
        setLoading(false);
        return;
      }
      
      toast.success(`Welcome back, ${user.name || user.email}!`);
      
      // Role-based redirect
      if (user.role === 'super_admin' || user.role === 'admin') {
        navigate('/admin/dashboard');
      } else if (user.role === 'agent') {
        navigate('/agent/dashboard');
      } else {
        navigate('/app/home'); // Regular users go to Super App Home
      }
    } catch (error) {
      const errorMsg = formatApiErrorDetail(error.response?.data?.detail) || error.message;
      if (errorMsg.toLowerCase().includes('too many')) {
        toast.error(errorMsg);
      } else if (errorMsg.toLowerCase().includes('invalid')) {
        toast.error('Invalid email or password. Please check your credentials.');
      } else {
        toast.error(errorMsg);
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-2">
      <div className="hidden lg:block relative overflow-hidden bg-zinc-900">
        <img
          src="https://images.unsplash.com/photo-1622737133809-d95047b9e673?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzOTB8MHwxfHNlYXJjaHwxfHxtaW5pbWFsaXN0JTIwYWJzdHJhY3QlMjAzZCUyMHNoYXBlcyUyMGNsZWFyJTIwYmFja2dyb3VuZHxlbnwwfHx8fDE3ODMzNTQ5MzR8MA&ixlib=rb-4.1.0&q=85"
          alt="Abstract 3D"
          className="w-full h-full object-cover opacity-50"
        />
        <div className="absolute inset-0 bg-gradient-to-br from-zinc-900/90 to-black/80 flex items-center justify-center p-12">
          <div className="max-w-lg">
            <h1 className="text-5xl font-semibold text-white mb-6" style={{ fontFamily: 'Outfit, sans-serif' }}>
              The Matrise
            </h1>
            <p className="text-xl text-zinc-300" style={{ fontFamily: 'Manrope, sans-serif' }}>
              Modern digital wallet platform with multi-currency support, instant transfers, and secure transactions.
            </p>
          </div>
        </div>
      </div>

      <div className="flex items-center justify-center p-8 bg-background">
        <div className="w-full max-w-md space-y-6">
          <div>
            <h2 className="text-3xl font-semibold text-foreground" style={{ fontFamily: 'Outfit, sans-serif' }} data-testid="login-heading">
              Sign In
            </h2>
            <p className="mt-2 text-muted-foreground" style={{ fontFamily: 'Manrope, sans-serif' }}>
              Choose your portal and enter your credentials.
            </p>
          </div>

          <Tabs value={portalType} onValueChange={setPortalType} className="w-full">
            <TabsList className="grid w-full grid-cols-3" data-testid="portal-tabs">
              <TabsTrigger value="user" data-testid="user-portal-tab" className="flex items-center gap-2">
                <User className="h-4 w-4" />
                User
              </TabsTrigger>
              <TabsTrigger value="agent" data-testid="agent-portal-tab" className="flex items-center gap-2">
                <Briefcase className="h-4 w-4" />
                Agent
              </TabsTrigger>
              <TabsTrigger value="admin" data-testid="admin-portal-tab" className="flex items-center gap-2">
                <Shield className="h-4 w-4" />
                Admin
              </TabsTrigger>
            </TabsList>

            <TabsContent value="user" className="mt-6">
              <div className="p-4 rounded-lg bg-blue-50 dark:bg-blue-950/30 border border-blue-200 dark:border-blue-900 mb-4">
                <p className="text-sm text-blue-800 dark:text-blue-300">
                  Sign in as a regular user to access your wallets, deposits, transfers, and more.
                </p>
              </div>
            </TabsContent>

            <TabsContent value="agent" className="mt-6">
              <div className="p-4 rounded-lg bg-indigo-50 dark:bg-indigo-950/30 border border-indigo-200 dark:border-indigo-900 mb-4">
                <p className="text-sm text-indigo-800 dark:text-indigo-300 font-medium mb-1">
                  Agent Portal Access
                </p>
                <p className="text-xs text-indigo-700 dark:text-indigo-400">
                  Sign in with your agent credentials to process deposits, withdrawals, and manage your float wallet.
                </p>
              </div>
            </TabsContent>

            <TabsContent value="admin" className="mt-6">
              <div className="p-4 rounded-lg bg-purple-50 dark:bg-purple-950/30 border border-purple-200 dark:border-purple-900 mb-4">
                <p className="text-sm text-purple-800 dark:text-purple-300 font-medium mb-1">
                  Administrator Access
                </p>
                <p className="text-xs text-purple-700 dark:text-purple-400">
                  Sign in with your administrator credentials to manage the platform, users, and system settings.
                </p>
              </div>
            </TabsContent>
          </Tabs>

          <form onSubmit={handleSubmit} className="space-y-5" data-testid="login-form">
            <div className="space-y-2">
              <Label htmlFor="email">Email Address</Label>
              <Input
                id="email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                placeholder="you@example.com"
                data-testid="login-email-input"
                className="h-11"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="password">Password</Label>
              <Input
                id="password"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                placeholder="••••••••"
                data-testid="login-password-input"
                className="h-11"
              />
            </div>

            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-2">
                <Checkbox
                  id="remember"
                  checked={rememberMe}
                  onCheckedChange={setRememberMe}
                  data-testid="login-remember-checkbox"
                />
                <Label htmlFor="remember" className="text-sm font-normal cursor-pointer">
                  Remember me
                </Label>
              </div>
              <Link to="/forgot-password" className="text-sm text-blue-600 hover:text-blue-700" data-testid="forgot-password-link">
                Forgot password?
              </Link>
            </div>

            <Button
              type="submit"
              className={`w-full h-11 ${portalType === 'admin' ? 'bg-purple-600 hover:bg-purple-700' : ''}`}
              disabled={loading}
              data-testid="login-submit-button"
            >
              {loading ? 'Signing in...' : `Sign In to ${portalType === 'admin' ? 'Admin' : 'User'} Portal`}
            </Button>
          </form>

          {portalType === 'user' && (
            <p className="text-center text-sm text-muted-foreground">
              Don't have an account?{' '}
              <Link to="/register" className="text-blue-600 hover:text-blue-700 font-medium" data-testid="register-link">
                Sign up
              </Link>
            </p>
          )}
        </div>
      </div>
    </div>
  );
};
